/**
 * MilestoneCelebrator.jsx — Feb 2026 · Cycle 63.
 *
 * Top-level side-effect component. Listens for progress-milestone
 * custom events and fires a small `celebrate()` burst + a warm toast
 * so ordinary progress feels magical WITHOUT any dopamine-farm dark
 * patterns (no leaderboards, no comparison, no push notifications).
 *
 * Milestones covered:
 *   · `we-first-star`      — first ⭐ EVER earned on this device
 *   · `we-first-badge`     — first 🏆 badge unlocked
 *   · `we-streak-milestone` — 3 / 7 / 14 / 30 / 100 day streak
 *
 * Mounted once inside App.js so it works across every route.
 * Renders nothing.
 */
import { useEffect } from "react";
import { toast } from "sonner";
import { celebrate } from "@/lib/celebrate";

export default function MilestoneCelebrator() {
  useEffect(() => {
    const onFirstStar = () => {
      celebrate();
      toast.success("🌟 Your very first star!", {
        description: "One tiny step — the map lights up from here.",
        duration: 4200,
      });
    };
    const onFirstBadge = (e) => {
      celebrate();
      toast.success(`🏆 First badge: ${e.detail?.label || "unlocked!"}`, {
        description: "There's a whole shelf waiting in your Passport.",
        duration: 4500,
      });
    };
    const onStreak = (e) => {
      const n = e.detail?.streak || 0;
      const msg = {
        3:   { title: "🔥 3-day streak!",   sub: "Small daily doses stick — proud of you." },
        7:   { title: "🌟 One week strong!", sub: "A whole week. That's how new habits are born." },
        14:  { title: "🚀 Two weeks in!",   sub: "Your brain is building bridges — keep going!" },
        30:  { title: "🎖 30 days!",         sub: "That's the mark of a real explorer." },
        100: { title: "👑 100 days!",        sub: "Legend status. Your future self is smiling." },
      }[n] || { title: `🔥 ${n}-day streak!`, sub: "Keep going!" };
      celebrate();
      toast.success(msg.title, { description: msg.sub, duration: 5000 });
    };
    window.addEventListener("we-first-star", onFirstStar);
    window.addEventListener("we-first-badge", onFirstBadge);
    window.addEventListener("we-streak-milestone", onStreak);
    return () => {
      window.removeEventListener("we-first-star", onFirstStar);
      window.removeEventListener("we-first-badge", onFirstBadge);
      window.removeEventListener("we-streak-milestone", onStreak);
    };
  }, []);
  return null;
}
